import { useState, useEffect, useCallback, useRef } from 'react';

const ACTIVITY_EVENTS = [
  'click',
  'keydown',
  'mousemove',
  'touchstart',
  'scroll',
  'focus',
] as const;

const THROTTLE_MS = 1000;

const DEFAULT_TIMEOUT_MS = 30 * 60 * 1000;     // 30 minuuttia
const DEFAULT_WARNING_MS = 5 * 60 * 1000;      // 5 min ennen timeoutia
const DEFAULT_CRITICAL_MS = 1 * 60 * 1000;     // 1 min ennen timeoutia

interface UseSessionTimeoutOptions {
  timeoutMs?: number;
  warningMs?: number;
  criticalMs?: number;
  onTimeout: () => void;
  onWarning?: () => void;
}

interface UseSessionTimeoutReturn {
  remainingTime: number;
  showWarningDialog: boolean;
  isInWarningZone: boolean;
  extendSession: () => void;
  logout: () => void;
}

export function useSessionTimeout({
  timeoutMs = DEFAULT_TIMEOUT_MS,
  warningMs = DEFAULT_WARNING_MS,
  criticalMs = DEFAULT_CRITICAL_MS,
  onTimeout,
  onWarning,
}: UseSessionTimeoutOptions): UseSessionTimeoutReturn {
  const [remainingTime, setRemainingTime] = useState(timeoutMs);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [isInWarningZone, setIsInWarningZone] = useState(false);
  
  const lastActivityRef = useRef(Date.now());
  const lastThrottleRef = useRef(0);
  const warningShownRef = useRef(false);
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setRemainingTime(timeoutMs);
    setShowWarningDialog(false);
    setIsInWarningZone(false);
    warningShownRef.current = false;
  }, [timeoutMs]);

  const extendSession = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  const logout = useCallback(() => {
    onTimeout();
  }, [onTimeout]);

  // Aktiivisuuden seuranta - throttled
  useEffect(() => {
    const handleActivity = () => {
      const now = Date.now();
      
      // Throttle: päivitä max kerran sekunnissa
      if (now - lastThrottleRef.current < THROTTLE_MS) {
        return;
      }
      
      lastThrottleRef.current = now;
      lastActivityRef.current = now;
      
      // Nollaa timer vain jos ei ole kriittisessä tilassa
      if (!showWarningDialog) {
        setRemainingTime(timeoutMs);
        setIsInWarningZone(false);
        warningShownRef.current = false;
      }
    };

    // Lisää event listenerit
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [timeoutMs, showWarningDialog]);

  // Pääajastin - laskee jäljellä olevaa aikaa
  useEffect(() => {
    intervalIdRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, timeoutMs - elapsed);
      
      setRemainingTime(remaining);

      // Tarkista varoitusraja (5 min ennen)
      if (remaining <= warningMs && remaining > criticalMs && !warningShownRef.current) {
        setIsInWarningZone(true);
        warningShownRef.current = true;
        onWarning?.();
      }

      // Tarkista kriittinen raja (1 min ennen)
      if (remaining <= criticalMs && remaining > 0) {
        setShowWarningDialog(true);
      }

      // Timeout - kirjaa ulos
      if (remaining === 0) {
        setShowWarningDialog(false);
        onTimeout();
      }
    }, 1000);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [timeoutMs, warningMs, criticalMs, onTimeout, onWarning]);

  return {
    remainingTime,
    showWarningDialog,
    isInWarningZone,
    extendSession,
    logout,
  };
}
